import type * as CSS from 'csstype';
import React from 'react';
import styled from 'styled-components';

import { nameof } from '../../utils/nameof';
import { ToTransientProps } from '../../utils/styled-helpers';

interface Props {
  'aria-label'?: string;
  alignItems?: CSS.Properties['alignItems'];
  as?: React.ComponentType<any>;
  backgroundColor?: CSS.Properties['backgroundColor'];
  children: React.ReactNode;
  className?: string;
  direction?: CSS.Properties['flexDirection'];
  flex?: CSS.Properties['flex'];
  flexWrap?: CSS.Properties['flexWrap'];
  inline?: boolean;
  height?: CSS.Properties['height'];
  justifyContent?: CSS.Properties['justifyContent'];
  margin?: CSS.Properties['margin'];
  padding?: CSS.Properties['padding'];
  spacing?: CSS.Properties['gap'] | number;
  onClick?: (event?: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  // Testing purpose, enabling setting "role" on the div element.
  role?: string;
  tabIndex?: number;
  title?: string;
  'aria-expanded'?: boolean;
  'aria-haspopup'?: React.AriaAttributes['aria-haspopup'];
}

const Stack = React.forwardRef<HTMLDivElement, Props>(
  (
    {
      spacing = 0,
      direction = 'column',
      margin = 0,
      height = 'auto',
      flexWrap = 'nowrap',
      padding = 0,
      backgroundColor = 'none',
      inline = false,
      ...props
    }: Props,
    ref,
  ) => {
    return (
      <Component
        $alignItems={props.alignItems}
        $backgroundColor={backgroundColor}
        $direction={direction}
        $flex={props.flex}
        $flexWrap={flexWrap}
        $height={height}
        $inline={inline}
        $justifyContent={props.justifyContent}
        $margin={margin}
        $padding={padding}
        $spacing={spacing}
        aria-expanded={props['aria-expanded']}
        aria-haspopup={props['aria-haspopup']}
        aria-label={props['aria-label']}
        as={props.as}
        className={props.className}
        ref={ref}
        role={props.role}
        tabIndex={props.tabIndex}
        title={props.title}
        onClick={props.onClick}
        onKeyDown={props.onKeyDown}
      >
        {props.children}
      </Component>
    );
  },
);

Stack.displayName = nameof({ Stack });

const Component = styled.div<
  // Required
  Required<
    ToTransientProps<
      Pick<
        Props,
        | 'padding'
        | 'spacing'
        | 'direction'
        | 'flexWrap'
        | 'margin'
        | 'backgroundColor'
        | 'inline'
        | 'height'
      >
    >
  > &
    // Optional
    ToTransientProps<Pick<Props, 'alignItems' | 'flex' | 'justifyContent'>>
>`
  display: ${(props) => (props.$inline ? 'inline-flex' : 'flex')};
  padding: ${(props) => props.$padding};
  flex: ${(props) => props.$flex};
  height: ${(props) => props.$height};
  flex-wrap: ${(props) => props.$flexWrap};
  flex-direction: ${(props) => props.$direction};
  gap: ${(props) => (typeof props.$spacing === 'number' ? `${props.$spacing}em` : props.$spacing)};
  align-items: ${(props) => props.$alignItems};
  justify-content: ${(props) => props.$justifyContent};
  background-color: ${(props) => props.$backgroundColor};

  margin: ${(props) => props.$margin};
`;

export { Stack, Component as StackStyled };
